import React, { useState } from "react";
import { publicLink } from "@/lib/landerDomain";
import { ArrowSquareOut, CheckCircle, CircleNotch, Eye, WarningCircle } from "@phosphor-icons/react";
import { AdminModal } from "@/components/pageSettings/AdminModal";
import { TestLeadResult } from "@/components/SendTestLeadButton";

const BTN = "inline-flex items-center gap-1 border border-black px-2 py-1 text-[10px] uppercase tracking-widest transition-colors duration-100 hover:bg-black hover:text-white";

export const fullyDelivered = (r) => (r?.attempts || []).length > 0 && r.attempts.every((a) => a.status === "delivered");

function ItemIcon({ item }) {
  if (item.state === "waiting") return <CircleNotch size={14} weight="bold" className="animate-spin text-zinc-500" />;
  if (item.state === "sent" && fullyDelivered(item.result)) return <CheckCircle size={14} weight="bold" className="text-emerald-700" />;
  return <WarningCircle size={14} weight="bold" className="text-amber-700" />;
}

function Item({ item, i, open, onOpen }) {
  const p = `check-all-item-${i}`;
  return (
    <li className="border-b border-zinc-200 py-2 last:border-b-0" data-testid={p}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <ItemIcon item={item} />
        <b>{item.row.title}</b>
        <span className="font-mono text-[10px] text-zinc-500">{item.row.path}</span>
        {item.state === "waiting" && <span className="text-[10.5px] text-zinc-500">Waiting…</span>}
        {item.result && (
          <button type="button" onClick={onOpen} className={`${BTN} ml-auto ${open ? "bg-black text-white" : ""}`} data-testid={`${p}-ty`}>
            <Eye size={11} weight="bold" /> {open ? "Showing thank-you" : "Thank-you page"}
          </button>
        )}
      </div>
      {item.result && <div className="mt-1.5"><TestLeadResult r={item.result} showLeadLink testId={p} /></div>}
      {item.error && <div className="mt-1 text-[11px] text-rose-700" data-testid={`${p}-error`}>{item.error}</div>}
    </li>
  );
}

// "Check all pages": one test lead per page, results listed together, any thank-you page viewable below.
export function CheckAllPagesModal({ batch, onClose }) {
  const [openIdx, setOpenIdx] = useState(null);
  const { page, items } = batch;
  const finished = items.filter((x) => x.state !== "waiting");
  const good = items.filter((x) => x.state === "sent" && fullyDelivered(x.result)).length;
  const done = finished.length === items.length;
  const url = openIdx !== null ? items[openIdx]?.result?.thank_you_path : null;
  return (
    <AdminModal eyebrow="Check all pages" title={page.firm_name} onClose={onClose} testId="check-all-modal" wide>
      <div className={`mb-2 border-2 p-2 text-xs ${!done ? "border-zinc-400" : good === items.length ? "border-emerald-700 bg-emerald-50" : "border-amber-700 bg-amber-50"}`} data-testid="check-all-summary">
        {done ? `${good} of ${items.length} pages reached every delivery.` : `Sending test lead ${finished.length + 1} of ${items.length}…`}
        <span className="text-zinc-600"> Each is a marked test lead, kept out of billing and reports.</span>
      </div>
      <ul data-testid="check-all-list">
        {items.map((it, i) => <Item key={it.row.key} item={it} i={i} open={openIdx === i} onOpen={() => setOpenIdx(openIdx === i ? null : i)} />)}
      </ul>
      {url && (
        <div className="mt-3" data-testid="check-all-ty">
          <div className="mb-1.5 flex flex-wrap items-center gap-2 text-[11px] text-zinc-600">
            Thank-you page from <b>{items[openIdx].row.title}</b> ({items[openIdx].row.thank_you_design})
            <a href={publicLink(url)} target="_blank" rel="noopener noreferrer" className={`${BTN} ml-auto`} data-testid="check-all-ty-open-tab"><ArrowSquareOut size={11} weight="bold" /> New tab</a>
          </div>
          <iframe key={url} title="Thank-you page" src={publicLink(url)} className="h-[60vh] w-full border-2 border-black bg-white" data-testid="check-all-ty-frame" />
        </div>
      )}
    </AdminModal>
  );
}
